import { useEffect, useMemo, useRef, useState } from 'react';
import axiosPrivate from 'config/axiosPrivate';
import axiosScoring from 'config/axiosScoring';
import { buildGapFromClientFreqV2 } from 'helpers/FormatKeywordsHelper';

const buildGapMapFromScore = (score) => {
  const fromClientFreq = buildGapFromClientFreqV2(
    score?.metrics?.semanticResult?.data
  );
  if (Array.isArray(fromClientFreq) && fromClientFreq.length > 0) {
    const result = {};
    fromClientFreq.forEach((item) => {
      const word = String(item?.word || '').trim();
      if (!word) return;
      const frequency = Number(item?.frequency);
      result[word] = Number.isFinite(frequency) && frequency > 0 ? frequency : 1;
    });
    if (Object.keys(result).length > 0) return result;
  }

  const semanticGap = score?.metrics?.semanticResult?.data?.semanticGap;
  if (!Array.isArray(semanticGap) || semanticGap.length === 0) return null;

  const result = {};
  semanticGap.forEach((item) => {
    if (!item) return;
    if (typeof item === 'string') {
      const match = item.match(/^(.+?)\((\d+)\)$/);
      if (match) {
        result[match[1].trim()] = parseInt(match[2], 10);
      } else if (item.trim()) {
        result[item.trim()] = 1;
      }
      return;
    }
    const word = (item.word || item.name || item.keyword || '').toString().trim();
    if (!word) return;
    const frequency = Number(item.frequency ?? item.count ?? item.total ?? 1);
    result[word] = Number.isFinite(frequency) && frequency > 0 ? frequency : 1;
  });

  return Object.keys(result).length > 0 ? result : null;
};

const isHealthyScoreData = (score) =>
  Boolean(score) && Number(score.globalScore) > 0;

const useAdminScoreData = (
  textId,
  analyzeEndpointUrl,
  analysisPayload,
  optionsOrDelay = 4000,
  legacySpinnerDelay = 2000,
  detailsEndpointPrefix = '/texts'
) => {
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initializedTextId, setInitializedTextId] = useState(undefined);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const initialized = !textId || initializedTextId === textId;

  const isInitialLoadRef = useRef(true);
  const hasRunFirstAnalyzeRef = useRef(false);

  const payloadKey = useMemo(() => {
    if (!analysisPayload) return '';

    try {
      const { html, text, keyword, gap, maillage_interne, language, country } =
        analysisPayload || {};
      return JSON.stringify({
        html,
        text,
        keyword,
        gap,
        maillage_interne,
        language,
        country,
      });
    } catch (_) {
      return JSON.stringify(analysisPayload);
    }
  }, [analysisPayload]);

  const lastPayloadKeyRef = useRef('');
  const payloadKeyRef = useRef(payloadKey);
  payloadKeyRef.current = payloadKey;
  const trackedTextIdRef = useRef(textId);

  if (trackedTextIdRef.current !== textId) {
    trackedTextIdRef.current = textId;
    lastPayloadKeyRef.current = '';
    hasRunFirstAnalyzeRef.current = false;
    isInitialLoadRef.current = true;
  }

  const analysisPayloadRef = useRef(analysisPayload);
  analysisPayloadRef.current = analysisPayload;
  const scoreDataRef = useRef(scoreData);
  scoreDataRef.current = scoreData;

  const options = useMemo(() => {
    if (typeof optionsOrDelay === 'number') {
      return {
        analyzeDelay: optionsOrDelay,
        initialAnalyzeDelay: optionsOrDelay,
        initialSpinnerDelay: 2000,
        analyzeSpinnerDelay: legacySpinnerDelay,
      };
    }
    return {
      analyzeDelay: optionsOrDelay?.analyzeDelay ?? 4000,
      initialAnalyzeDelay: optionsOrDelay?.initialAnalyzeDelay ?? 10000,
      initialSpinnerDelay: optionsOrDelay?.initialSpinnerDelay ?? 2000,
      analyzeSpinnerDelay: optionsOrDelay?.analyzeSpinnerDelay ?? 2000,
      disableAnalyze: optionsOrDelay?.disableAnalyze ?? false,
    };
  }, [optionsOrDelay, legacySpinnerDelay]);

  useEffect(() => {
    if (!textId) return undefined;

    let isMounted = true;
    let spinnerTimer;

    const fetchInitial = async () => {
      try {
        if (options.initialSpinnerDelay > 0) {
          spinnerTimer = setTimeout(() => {
            if (isMounted) setLoading(true);
          }, options.initialSpinnerDelay);
        }

        const response = await axiosPrivate.get(
          `${detailsEndpointPrefix}/${textId}/fullDetails`
        );

        if (!isMounted) return;

        if (response?.status === 200 && response?.data?.contentScore) {
          const nextScore = response.data.contentScore;
          // Ne pas écraser un bon score local avec un contentScore vidé (globalScore 0).
          if (
            isHealthyScoreData(nextScore) ||
            !isHealthyScoreData(scoreDataRef.current)
          ) {
            setScoreData(nextScore);
          }
          setError(null);
        } else if (!isHealthyScoreData(scoreDataRef.current)) {
          setScoreData({});
          setError(null);
        }
      } catch (err) {
        if (!isMounted) return;

        const errorMessage = err.response?.data || err.message || 'Failed to fetch score data';
        const isTextNotExistError = typeof errorMessage === 'string' &&
          (errorMessage.toLowerCase().includes("this text doesn't exist") ||
            errorMessage.toLowerCase().includes("texte n'existe pas"));

        if (isTextNotExistError) {
          setScoreData({});
          setError(null);
        } else {
          setError(errorMessage);
        }
      } finally {
        clearTimeout(spinnerTimer);
        if (!isMounted) return;
        setLoading(false);
        isInitialLoadRef.current = false;
        lastPayloadKeyRef.current = '';
        setInitializedTextId(textId);
      }
    };

    fetchInitial();

    return () => {
      isMounted = false;
      clearTimeout(spinnerTimer);
    };
  }, [textId, options.initialSpinnerDelay, detailsEndpointPrefix]);

  useEffect(() => {
    if (options.disableAnalyze) {
      return;
    }

    if (!analysisPayload || !payloadKey) return;

    if (textId && !initialized) {
      return;
    }

    const isFirstAnalyze = !hasRunFirstAnalyzeRef.current;

    const hasPayloadChanged = payloadKey !== lastPayloadKeyRef.current;

    if (!hasPayloadChanged) {
      return;
    }

    let isMounted = true;
    let analyzeTimer;
    let spinnerTimer;

    const delay = isFirstAnalyze
      ? options.initialAnalyzeDelay
      : options.analyzeDelay;

    const runAnalyze = async () => {
      const latestPayload = analysisPayloadRef.current || {};

      // Si le payload a encore gap:{} (combinedKeywords pas prêt) mais que
      // fullDetails a déjà un contentScore avec gap, le réinjecter.
      // Si aucun gap n'existe vraiment, on laisse {}.
      let payloadToSend = latestPayload;
      const currentGap = latestPayload.gap;
      const gapIsEmpty =
        !currentGap ||
        (typeof currentGap === 'object' && Object.keys(currentGap).length === 0);
      if (gapIsEmpty) {
        const gapFromScore = buildGapMapFromScore(scoreDataRef.current);
        if (gapFromScore) {
          payloadToSend = { ...latestPayload, gap: gapFromScore };
        }
      }

      hasRunFirstAnalyzeRef.current = true;

      try {
        setIsAnalyzing(true);

        if (options.analyzeSpinnerDelay > 0) {
          spinnerTimer = setTimeout(() => {
            if (isMounted) setLoading(true);
          }, options.analyzeSpinnerDelay);
        }

        const response = await axiosScoring.post(
          analyzeEndpointUrl,
          payloadToSend,
          {
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (!isMounted) return;

        const nextScore = response?.data || null;
         if (
          isHealthyScoreData(nextScore) ||
          !isHealthyScoreData(scoreDataRef.current)
        ) {
          setScoreData(nextScore);
        }
        setError(null);
      } catch (err) {
        if (!isMounted) return;

        setError(err);
      } finally {
        clearTimeout(spinnerTimer);
        if (isMounted) setLoading(false);
        if (isMounted) setIsAnalyzing(false);
      }
    };

    analyzeTimer = setTimeout(runAnalyze, delay);
    lastPayloadKeyRef.current = payloadKey;

    return () => {
      isMounted = false;
      clearTimeout(analyzeTimer);
      clearTimeout(spinnerTimer);
    };
  }, [
    payloadKey,
    analyzeEndpointUrl,
    textId,
    options.analyzeDelay,
    options.initialAnalyzeDelay,
    options.analyzeSpinnerDelay,
    options.disableAnalyze,
    initialized,
  ]);

  useEffect(() => {
    setError(null);
    setIsAnalyzing(false);
  }, [textId]);

  return { scoreData, loading, error, initialized, isAnalyzing };
};

export default useAdminScoreData;
